// src/components/subscription/CreditPurchaseCheckout.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CREDIT_PACKAGES } from './config/constants';
import { formatCurrency } from '../../../utils/currency';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
// import api from '../../utils/api';
// import { getAuthHeader } from '../../utils/authToken';

import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  //   timeout: 5000,
  timeout: 30000, // Increase to 30 seconds
});

const token = localStorage.getItem('ownerToken');

const CreditCheckoutContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);

  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [setupIntent, setSetupIntent] = useState(null);
  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState(false);
  const [updatingDefault, setUpdatingDefault] = useState(false);

  const {
    type,
    packageId,
    package: creditPackage,
    amount,
  } = location.state || {};

  useEffect(() => {
    if (!type || (!packageId && !amount)) {
      navigate('/owner/test/credits');
      return;
    }
    fetchPaymentMethods();
  }, [type, packageId, amount, navigate]);

  // const fetchPaymentMethods = async () => {
  //   try {
  //     const response = await api.get('/subscriptions/payment-methods', {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });

  //     setPaymentMethods(response.data.paymentMethods);
  //     if (response.data.defaultPaymentMethod) {
  //       setSelectedPaymentMethod(response.data.defaultPaymentMethod);
  //     } else {
  //       setShowNewCardForm(true);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching payment methods:', error);
  //     toast.error('Failed to load payment methods');
  //   }
  // };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!stripe || loading || (!selectedPaymentMethod && !showNewCardForm))
  //     return;

  //   setLoading(true);
  //   setError(null);

  //   try {
  //     let paymentMethodId = selectedPaymentMethod?.id;

  //     if (showNewCardForm) {
  //       const { error: cardError } = await elements
  //         .getElement(CardElement)
  //         .submit();
  //       if (cardError) throw cardError;

  //       const { token, error: tokenError } = await stripe.createToken(
  //         elements.getElement(CardElement)
  //       );
  //       if (tokenError) throw tokenError;

  //       paymentMethodId = token.id;
  //     }

  //     const endpoint =
  //       type === 'package'
  //         ? '/subscriptions/credits/package'
  //         : '/subscriptions/credits/purchase';

  //     const data =
  //       type === 'package'
  //         ? { packageId, paymentMethodId }
  //         : { amount, paymentMethodId };

  //     const response = await api.post(endpoint, data, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });

  //     toast.success('Credits purchased successfully!');
  //     navigate('/owner/test/membershipplans');
  //   } catch (error) {
  //     console.error('Error purchasing credits:', error);
  //     setError(error.message || 'Failed to purchase credits');
  //     toast.error('Failed to purchase credits');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/subscriptions/payment-methods', {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPaymentMethods(response.data.paymentMethods);
      setSetupIntent(response.data.setupIntent);
      if (response.data.defaultPaymentMethod) {
        setDefaultPaymentMethodId(response.data.defaultPaymentMethod.id);
        setSelectedPaymentMethod(response.data.defaultPaymentMethod);
      } else {
        setShowNewCardForm(true);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error('Failed to load payment methods');
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (
      !window.confirm('Are you sure you want to delete this payment method?')
    ) {
      return;
    }

    setDeletingPaymentMethod(true);
    try {
      await api.delete(`/subscriptions/payment-methods/${methodId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchPaymentMethods();
      toast.success('Payment method deleted successfully');
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error(
        error.response?.data?.error || 'Failed to delete payment method'
      );
    } finally {
      setDeletingPaymentMethod(false);
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    setUpdatingDefault(true);
    try {
      await api.put(
        `/subscriptions/payment-methods/${methodId}`,
        { isDefault: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchPaymentMethods();
      toast.success('Default payment method updated');
    } catch (error) {
      console.error('Error updating default payment method:', error);
      toast.error(
        error.response?.data?.error || 'Failed to update default payment method'
      );
    } finally {
      setUpdatingDefault(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || loading || (!selectedPaymentMethod && !showNewCardForm))
      return;

    setLoading(true);
    setError(null);

    try {
      let paymentMethodId;

      if (showNewCardForm) {
        const { error: cardError, setupIntent: confirmedSetup } =
          await stripe.confirmCardSetup(setupIntent, {
            payment_method: {
              card: elements.getElement(CardElement),
            },
          });

        if (cardError) {
          throw cardError;
        }

        paymentMethodId = confirmedSetup.payment_method;

        // If this is the first payment method, set it as default
        if (paymentMethods.length === 0) {
          try {
            await api.put(
              `/subscriptions/payment-methods/${paymentMethodId}`,
              { isDefault: true },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (defaultError) {
            console.error(
              'Error setting default payment method:',
              defaultError
            );
          }
        }
      } else {
        paymentMethodId = selectedPaymentMethod.id;
      }

      const endpoint =
        type === 'package'
          ? '/subscriptions/credits/package'
          : '/subscriptions/credits/purchase';

      const data =
        type === 'package'
          ? { packageId, paymentMethodId }
          : { amount, paymentMethodId };

      const response = await api.post(endpoint, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Credits purchased successfully!');
      navigate('/owner/test/membership');
    } catch (error) {
      console.error('Error purchasing credits:', error);
      setError(error.message || 'Failed to purchase credits');
      toast.error('Failed to purchase credits');
    } finally {
      setLoading(false);
    }
  };

  const PaymentMethodCard = ({
    method,
    isDefault,
    onDelete,
    onSetDefault,
    isLoading,
    isSelected,
    onSelect,
  }) => {
    return (
      <div
        className={`border rounded-lg p-4 cursor-pointer ${
          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
        }`}
        onClick={onSelect}
      >
        <div className='flex items-center justify-between'>
          <div className='flex-1'>
            <p className='font-medium flex items-center gap-2'>
              {method.card.brand.toUpperCase()} •••• {method.card.last4}
              {isDefault && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                  Default
                </span>
              )}
            </p>
            <p className='text-sm text-gray-600'>
              Expires {method.card.exp_month}/{method.card.exp_year}
            </p>
          </div>
          <div className='flex items-center gap-2'>
            {!isDefault && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSetDefault(method.id);
                }}
                disabled={isLoading}
                className='px-3 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50'
              >
                Set as Default
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(method.id);
              }}
              disabled={isLoading}
              className='px-3 py-1 text-sm text-red-600 hover:text-red-800 disabled:opacity-50'
            >
              Delete
            </button>
            <input
              type='radio'
              checked={isSelected}
              onChange={() => onSelect()}
              className='h-4 w-4 text-blue-600'
            />
          </div>
        </div>
      </div>
    );
  };

  const calculateTotal = () => {
    if (type === 'package') {
      return creditPackage.prices.usd.amount;
    }
    return amount * 10; // $0.10 per credit in cents
  };

  return (
    <div className='max-w-2xl mx-auto p-8'>
      <h2 className='text-2xl font-bold mb-6'>Complete Credit Purchase</h2>

      {/* Purchase Summary */}
      <div className='bg-gray-50 rounded-lg p-6 mb-8'>
        <h3 className='text-lg font-semibold mb-4'>Purchase Summary</h3>
        <div className='space-y-2'>
          <div className='flex justify-between'>
            <span>
              {type === 'package' ? creditPackage.name : `${amount} Credits`}
            </span>
            <span>{formatCurrency(calculateTotal() / 100)}</span>
          </div>

          <div className='flex justify-between text-sm text-gray-600'>
            <span>Price per credit</span>
            <span>{formatCurrency(0.1)}</span>
          </div>

          <div className='border-t pt-2 mt-2'>
            <div className='flex justify-between font-semibold'>
              <span>Total</span>
              <span>{formatCurrency(calculateTotal() / 100)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <form onSubmit={handleSubmit} className='space-y-6'>
        <div>
          <h3 className='text-lg font-semibold mb-4'>Payment Method</h3>

          {/* {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`border rounded-lg p-4 mb-4 cursor-pointer ${
                selectedPaymentMethod?.id === method.id
                  ? 'border-blue-500 bg-blue-50'
                  : ''
              }`}
              onClick={() => {
                setSelectedPaymentMethod(method);
                setShowNewCardForm(false);
              }}
            >
              <div className='flex items-center justify-between'>
                <div>
                  <p className='font-medium'>
                    {method.card.brand.toUpperCase()} •••• {method.card.last4}
                  </p>
                  <p className='text-sm text-gray-600'>
                    Expires {method.card.exp_month}/{method.card.exp_year}
                  </p>
                </div>
                <input
                  type='radio'
                  checked={selectedPaymentMethod?.id === method.id}
                  onChange={() => {}}
                  className='h-4 w-4 text-blue-600'
                />
              </div>
            </div>
          ))} */}

          {paymentMethods.map((method) => (
            <PaymentMethodCard
              key={method.id}
              method={method}
              isDefault={method.id === defaultPaymentMethodId}
              isSelected={selectedPaymentMethod?.id === method.id}
              onSelect={() => {
                setSelectedPaymentMethod(method);
                setShowNewCardForm(false);
              }}
              onDelete={handleDeletePaymentMethod}
              onSetDefault={handleSetDefaultPaymentMethod}
              isLoading={deletingPaymentMethod || updatingDefault}
            />
          ))}

          {/* <div
            className={`border rounded-lg p-4 cursor-pointer ${
              showNewCardForm ? 'border-blue-500 bg-blue-50' : ''
            }`}
            onClick={() => {
              setShowNewCardForm(true);
              setSelectedPaymentMethod(null);
            }}
          >
            <div className='flex items-center justify-between'>
              <p className='font-medium'>Add new card</p>
              <input
                type='radio'
                checked={showNewCardForm}
                onChange={() => {}}
                className='h-4 w-4 text-blue-600'
              />
            </div>

            {showNewCardForm && (
              <div className='mt-4'>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>
            )}
          </div> */}
          {/* Add New Card Option */}
          <div
            className={`border rounded-lg p-4 cursor-pointer ${
              showNewCardForm ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}
            onClick={() => {
              setShowNewCardForm(true);
              setSelectedPaymentMethod(null);
            }}
          >
            <div className='flex items-center justify-between'>
              <p className='font-medium'>Add new card</p>
              <input
                type='radio'
                checked={showNewCardForm}
                onChange={() => {}}
                className='h-4 w-4 text-blue-600'
              />
            </div>

            {showNewCardForm && (
              <div className='mt-4'>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {error && <div className='text-red-500 text-sm'>{error}</div>}

        <button
          type='submit'
          disabled={loading || (!selectedPaymentMethod && !showNewCardForm)}
          className='w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 
                   disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {loading ? (
            <div className='flex items-center justify-center'>
              <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2' />
              Processing...
            </div>
          ) : (
            `Pay ${formatCurrency(calculateTotal() / 100)}`
          )}
        </button>
      </form>
    </div>
  );
};

const CreditPurchaseCheckout = () => {
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  );

  return (
    <Elements stripe={stripePromise}>
      <CreditCheckoutContent />
    </Elements>
  );
};

export default CreditPurchaseCheckout;
